<template>
  <div class="fill-height">
    <scatter-chart
      v-if="viewMode === 'chart'"
      class="fill-height"
      :series="getSeries()"
      :xaxis="{
        tickAmount: 10,
        min: 0,
        max: getMaxValue('UXPCSEEF')
      }"
      :annotations="getAnnotations()"
    >
      <template v-slot:header>
        <v-card-title class="pa-6 d-flex justify-space-between">
          <v-row
            dense
            align="center"
          >
            <v-col class="text-no-wrap">
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('card_title.team_explosive_vs_hsr_min') }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
              </div>
            </v-col>
          </v-row>
          <v-btn
            v-if="removable"
            icon
            style="pointer-events: auto"
            @click="$emit('remove')"
          >
            <v-icon small>
              fal fa-times
            </v-icon>
          </v-btn>
          <visual-list-toggle
            v-else
            :value.sync="viewMode"
          />
        </v-card-title>
      </template>
    </scatter-chart>

    <v-card
      v-else-if="viewMode === 'table'"
      class="fill-height rounded-lg"
    >
      <v-card-title class="pa-6 d-flex justify-space-between">
        <div class="subtitle-2 grey--text text--grey-100">
          {{ $t('card_title.team_explosive_vs_hsr_min') }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
        </div>
        <div class="d-flex align-center">
          <visual-list-toggle
            v-if="!removable"
            :value.sync="viewMode"
          />
        </div>
      </v-card-title>

      <chart-list-view-table
        :headers="tableHeaders"
        :items="playersTableData"
      >
        <template v-slot:item.name="{ item }">
          {{ item.last_name }}, {{ item.first_name }}
        </template>
      </chart-list-view-table>
    </v-card>
  </div>
</template>

<script>
import ScatterChart from '@/components/charts/ScatterChart.vue'
import VisualListToggle from '@/components/buttons/VisualListToggle.vue'
import ChartListViewTable from '@/components/charts/implementation/ChartListViewTable.vue'

export default {
  name: 'ExplosiveHsrMinChart',
  components: { ChartListViewTable, ScatterChart, VisualListToggle },
  props: {
    players: {
      type: Array,
      required: true
    },
    removable: {
      type: Boolean,
      default: false
    },
    aggregation: {
      type: String,
      default: null
    }
  },
  data: () => ({
    viewMode: 'chart'
  }),
  computed: {
    playersTableData() {
      return this.players.map(p => {
        const stats = this.aggregation && !this.removable ? p.latest_stats : p.player_stats
        const explosiveKey = this.getKey('UXPCSEEF')
        const hsrMinKey = this.getKey('UXHSRmin')

        return {
          ...p,
          explosive: stats?.[explosiveKey] || 0,
          hsrMin: stats?.[hsrMinKey] || 0
        }
      })
    },
    tableHeaders() {
      return [
        {
          text: this.$t('table_headers.name'),
          value: 'name',
          sortable: true,
          width: '40%'
        },
        {
          text: this.$t('variables.UXPCSEEF'),
          value: 'explosive',
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('variables.UXHSRmin'),
          value: 'hsrMin',
          sortable: true,
          align: 'center'
        }
      ]
    }
  },
  methods: {
    getKey(baseKey) {
      return this.aggregation && !this.removable ? `${baseKey}_${this.aggregation}` : baseKey
    },

    getSeries() {
      return this.players.map(p => {
        const stats = this.aggregation && !this.removable ? p.latest_stats : p.player_stats
        const explosiveKey = this.getKey('UXPCSEEF')
        const hsrMinKey = this.getKey('UXHSRmin')

        return {
          name: p.last_name + ', ' + p.first_name,
          data: [
            [stats?.[explosiveKey] || 0, stats?.[hsrMinKey] || 0]
          ]
        }
      })
    },

    getMaxValue(baseKey) {
      const key = this.getKey(baseKey)
      const statsKey = this.aggregation && !this.removable ? 'latest_stats' : 'player_stats'

      return Math.max(...this.players.map(p => p[statsKey]?.[key] || 0), 1)
    },

    getAnnotations() {
      const maxExplosive = this.getMaxValue('UXPCSEEF') / 2
      const maxHsrMin = this.getMaxValue('UXHSRmin') / 2

      return {
        xaxis: [{
          x: maxExplosive,
          strokeDashArray: 0,
          borderColor: '#008FFB'
        }],
        yaxis: [{
          y: maxHsrMin,
          strokeDashArray: 0,
          borderColor: '#008FFB'
        }]
      }
    }
  }
}
</script>
